import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import { VisibilityType } from '../../../api/personas'
import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import { CarouselForm, CarouselItem } from '../CarouselForm'
import { Chip, ChipProps } from '../Chip'
import { LockIcon, VisibilityIcon, VisibilityOffIcon } from '../icons'
import './styles.css'

export interface CarouselEditCardProps {
  id: string
  collapsed?: boolean
  data: CarouselItem
  onItemRemove?: (data: CarouselItem) => void
  onItemUpdate?: (data: CarouselItem) => void
  dragHandleProps?: {
    [key: string]: unknown
  }
  isLoading?: boolean
}

const iconLabelMap: Record<
  VisibilityType,
  { icon: React.ReactNode; label: string; color: ChipProps['color'] }
> = {
  public: {
    label: 'All users',
    icon: <VisibilityIcon />,
    color: 'default',
  },
  admin: {
    label: 'Admins only',
    icon: <LockIcon />,
    color: 'secondary',
  },
  hidden: {
    label: 'Hidden',
    icon: <VisibilityOffIcon />,
    color: 'error',
  },
  visitor: {
    label: 'Visitors only',
    icon: <VisibilityIcon />,
    color: 'primary',
  },
}

export function CarouselEditCard({
  id,
  data,
  collapsed,
  onItemRemove,
  onItemUpdate,
  dragHandleProps,
  isLoading = false,
}: CarouselEditCardProps) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(collapsed || false)
  const [contentHeight, setContentHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsCollapsed(collapsed || false)
  }, [collapsed])

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const updateHeight = () => {
      setContentHeight(el.scrollHeight + 16)
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(el)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const handleRemove: React.MouseEventHandler<HTMLButtonElement> = ev => {
    ev.stopPropagation()
    onItemRemove?.(data)
  }

  return (
    <div className="relative carousel-edit-card">
      <div
        className={clsx(
          '-z-10 absolute bottom-0 left-0 right-0 tc-mv-blur top-0',
          {
            'bg-[#6B6B6B]': data.sourceType === 'Companion',
            'bg-blue/10': data.sourceType === 'Banner',
          },
        )}
      ></div>
      <div className="p-4">
        {isLoading && !isCollapsed && (
          <div className="absolute inset-0 flex items-center justify-center">
            <AnimatedSpinner />
          </div>
        )}
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <div className="flex items-center truncate">
            {dragHandleProps && (
              <button
                type="button"
                className="btn btn-small bg-gray-500/30 hover:bg-gray-700/30 mr-3 drag-handle"
                {...dragHandleProps}
                onClick={e => e.stopPropagation()}
              >
                <i className="fa-solid fa-grip-vertical" />
              </button>
            )}
            <h2 className="text-lg font-medium truncate pr-2">
              <span className="uppercase">
                {data.sourceType !== 'Banner'
                  ? data.sourceType === 'Companion'
                    ? 'Companion:\u00A0'
                    : 'Watch Session:\u00A0'
                  : 'Banner:\u00A0'}
              </span>
              {data.title ||
                `${data.companion?.firstName || ''} ${data.companion?.lastName || ''}${data.companion?.jobTitle ? ` - ${data.companion?.jobTitle}` : ''}`.trim() ||
                'UNTITLED'}
            </h2>
          </div>

          <span className="flex items-center">
            {isCollapsed && data.visibility && (
              <Chip
                icon={iconLabelMap[data.visibility].icon}
                label={iconLabelMap[data.visibility].label}
                color={iconLabelMap[data.visibility].color}
                className="!mr-2"
              />
            )}

            <button
              type="button"
              className="btn btn-small bg-gray-500/30 hover:bg-gray-700/30 mr-4"
              onClick={handleRemove}
            >
              <i className="fa-solid fa-trash" />
            </button>
            <span
              className={`transform transition-transform duration-300 ease-in-out ${isCollapsed ? 'rotate-180' : ''}`}
            >
              <i className="fa-solid fa-angle-down" />
            </span>
          </span>
        </div>
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{
            maxHeight: isCollapsed ? '0px' : `${contentHeight}px`,
            opacity: isCollapsed ? 0 : 1,
          }}
        >
          <div ref={contentRef}>
            <CarouselForm id={id} value={data} onSubmit={onItemUpdate} />
          </div>
        </div>
      </div>
    </div>
  )
}
